/**
 * Central-auth-origin redirect composition — pure, unit-tested helpers.
 *
 * Google + Apple forbid wildcard OAuth origins, so SSO runs on ONE
 * registered origin: `login.3mote.com` (prod) / `login-staging.3mote.com`
 * (staging), served by the same SPA bundle. Deployed tenant hosts send
 * every logged-out visitor there (the root route's guard and the tenant
 * `/login` redirect); after auth, the central page sends the user back to
 * a tenant subdomain. Everything that composes a URL for either direction
 * lives here so it can be tested without a DOM.
 *
 * OPEN-REDIRECT GUARDS — two independent ones:
 *
 *   - The post-auth HOST is only ever composed from a `subdomain` taken
 *     out of the authenticated `GET /api/me/tenants` membership list. The
 *     `?return=` search param is used solely as a LOOKUP KEY into that
 *     list (`resolveReturnTenant`) — a value that matches no membership is
 *     discarded, so unvalidated input can never become a navigation host.
 *   - The post-auth PATH (`?next=`, which lets a deep link survive the
 *     central hop) is a same-origin path or nothing: `sanitizeNextPath`
 *     rejects anything that could re-target the host and the result is
 *     only ever appended to an origin this module composed itself.
 */

import { CENTRAL_AUTH_LABEL, isCentralAuthHost, isLocalhostHost } from '@/auth/host-parser'

const APEX = '.3mote.com'

/** One row of the authenticated `GET /api/me/tenants` payload. */
export interface TenantMembership {
  subdomain: string
  name: string
  logoUrl: string | null
  role: string
}

/**
 * Belt-and-braces shape check on server-provided subdomains before they
 * are interpolated into a URL. The BE already constrains slugs at tenant
 * creation; this guard means even a corrupted row can't smuggle a path,
 * dot, or scheme into `https://{subdomain}{suffix}.3mote.com/`.
 */
export const SAFE_TENANT_SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i

export function filterSafeMemberships(tenants: TenantMembership[]): TenantMembership[] {
  return tenants.filter((tenant) => SAFE_TENANT_SUBDOMAIN_RE.test(tenant.subdomain))
}

/**
 * `-staging` iff the current central host is the staging alias. The
 * suffix decides which stage's tenant hosts the redirect targets —
 * prod central (`login.3mote.com`) sends to suffix-less prod hosts.
 */
export function stageSuffixForCentralHost(host: string): '' | '-staging' {
  const hostname = host.split(':')[0] ?? ''
  return hostname === `${CENTRAL_AUTH_LABEL}-staging${APEX}` ? '-staging' : ''
}

/**
 * Absolute origin URL (with trailing `/`) for a tenant subdomain, staged
 * to match the central host the user is currently on. Throws on a
 * subdomain that fails the shape check — callers pass memberships
 * through `filterSafeMemberships` first, so a throw is a programming
 * error, not a user-reachable state.
 */
export function tenantOriginForSubdomain(centralHost: string, subdomain: string): string {
  if (!SAFE_TENANT_SUBDOMAIN_RE.test(subdomain)) {
    throw new Error(`Refusing to compose a redirect for unsafe subdomain: ${subdomain}`)
  }
  return `https://${subdomain}${stageSuffixForCentralHost(centralHost)}${APEX}/`
}

const NEXT_PATH_MAX_LENGTH = 2048

/**
 * Auth routes never make sense as a post-sign-in landing: `/login` on a
 * tenant host redirects straight back to the central origin (a loop),
 * `/logout` would sign the user out again, and the rest are pre-auth
 * surfaces. `/accept-invite` belongs to a different sign-in path entirely.
 */
const NEXT_PATH_EXCLUDED_ROUTES = [
  '/login',
  '/logout',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
] as const

/** Exactly ONE leading slash — rejects `//host` and `/\host` up front. */
const NEXT_PATH_LEAD_RE = /^\/(?![/\\])/

/** C0 controls (U+0000–U+001F) and DEL (U+007F) — header / URL smuggling material. */
function hasControlCharacters(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i)
    if (code < 0x20 || code === 0x7f) return true
  }
  return false
}

/**
 * Validate a `?next=` deep-link path before it rides through the central
 * hop. Returns the input VERBATIM when it is safe to append to a tenant
 * origin, else null — callers drop the param; there is no fallback value.
 *
 * The threat is an open redirect: `next` is attacker-controllable (a
 * plain search param on a public URL), so this is a strict allowlist of
 * what a same-origin SPA path looks like, not a denylist of known tricks:
 *
 *   - a string, non-empty, at most 2048 chars (URL-length hygiene);
 *   - exactly one leading slash — `//evil.com` is a protocol-relative URL
 *     and browsers normalize `/\evil.com` to the same thing; anything
 *     carrying a scheme (`https:`, `javascript:`) fails this test too;
 *   - no backslash anywhere (browsers treat `\` as `/` inside URLs) and
 *     no control characters;
 *   - the pathname (before the first `?` or `#`) is not bare `/` — there
 *     is nothing to preserve, the tenant origin already lands there — and
 *     is not one of the auth routes listed above.
 *
 * The value is deliberately not URL-decoded or normalized: it is checked
 * as the browser would navigate it, and it is re-encoded by
 * `URLSearchParams` when it rides `?next=`.
 */
export function sanitizeNextPath(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  if (raw.length === 0 || raw.length > NEXT_PATH_MAX_LENGTH) return null
  if (!NEXT_PATH_LEAD_RE.test(raw)) return null
  if (raw.includes('\\') || hasControlCharacters(raw)) return null
  // A bare root carries nothing worth a hop; `/?selectedDeliverable=…`
  // (a master-board card deep link) or `/#…` does, so only the exact `/`
  // is dropped.
  if (raw === '/') return null
  // The router matches routes case-insensitively, so `/LOGOUT` would
  // serve the logout route — compare lower-cased, return `raw` verbatim.
  const pathname = (raw.split(/[?#]/, 1)[0] ?? '').toLowerCase()
  for (const route of NEXT_PATH_EXCLUDED_ROUTES) {
    if (pathname === route || pathname.startsWith(`${route}/`)) return null
  }
  return raw
}

/**
 * Resolve a `?return=<subdomain>` param against the membership list.
 * Match → that membership wins over the picker/auto-redirect logic.
 * No match (or no param) → null; the param is never used any other way.
 */
export function resolveReturnTenant(
  returnParam: string | undefined,
  tenants: TenantMembership[],
): TenantMembership | null {
  if (!returnParam) return null
  const wanted = returnParam.trim().toLowerCase()
  if (!wanted) return null
  return tenants.find((tenant) => tenant.subdomain.toLowerCase() === wanted) ?? null
}

export type CentralDestination =
  | { kind: 'redirect'; tenant: TenantMembership; viaReturn: boolean }
  | { kind: 'picker' }
  | { kind: 'none' }

/**
 * Post-auth decision on the central origin:
 *   - a validated `?return=` match wins outright (`viaReturn: true`);
 *   - exactly one membership → auto-redirect (`viaReturn: false`);
 *   - several → workspace picker;
 *   - zero → "no workspace" state.
 *
 * `viaReturn` gates the `?next=` deep link: that path came from the tenant
 * named in `?return=`, so it is only meaningful when THAT tenant is the
 * destination. A single-membership auto-redirect (which may be a different
 * tenant than the one that sent the user) and a picker choice never carry
 * it — a path from tenant A must never be applied to tenant B.
 */
export function decideCentralDestination(
  tenants: TenantMembership[],
  returnParam: string | undefined,
): CentralDestination {
  const returned = resolveReturnTenant(returnParam, tenants)
  if (returned) return { kind: 'redirect', tenant: returned, viaReturn: true }
  const only = tenants.length === 1 ? tenants[0] : undefined
  if (only) return { kind: 'redirect', tenant: only, viaReturn: false }
  if (tenants.length === 0) return { kind: 'none' }
  return { kind: 'picker' }
}

/**
 * Where the central page sends a signed-in user: the staged tenant origin
 * plus, when the caller passes one, the guarded `?next=` path. `next` is
 * re-checked here — not only where the param was parsed — so no caller can
 * append a raw value by mistake; a rejected value degrades to the origin
 * root, never to an error the user would see.
 */
export function composeWorkspaceUrl(centralHost: string, subdomain: string, next?: string): string {
  const origin = tenantOriginForSubdomain(centralHost, subdomain)
  const safeNext = sanitizeNextPath(next)
  return safeNext === null ? origin : `${origin}${safeNext.slice(1)}`
}

/**
 * The central host that serves a given tenant host's stage:
 * `aeeiee-staging.3mote.com` → `login-staging.3mote.com`; a suffix-less
 * prod tenant → `login.3mote.com`. Returns null for localhost (dev keeps
 * the inline login page) and for non-`.3mote.com` hosts (CI previews —
 * caller falls back to inline there too).
 */
export function centralHostForTenantHost(host: string): string | null {
  if (isLocalhostHost(host)) return null
  const hostname = host.split(':')[0] ?? ''
  if (!hostname.endsWith(APEX)) return null
  if (isCentralAuthHost(hostname)) return hostname
  const label = hostname.slice(0, -APEX.length)
  const suffix = label.endsWith('-staging') ? '-staging' : ''
  return `${CENTRAL_AUTH_LABEL}${suffix}${APEX}`
}

export interface CentralLoginUrlOptions {
  /**
   * Same-origin path to land on after sign-in (`/board/…?x=1#c`). Appended
   * only when `sanitizeNextPath` accepts it; silently dropped otherwise.
   */
  next?: string
}

/**
 * The URL a deployed tenant host sends a logged-out visitor to:
 * `https://login{-staging}.3mote.com/login?return={tenantSub}[&next=…]`.
 * `return` round-trips the CURRENT tenant so the central page can send
 * the user straight back after auth (validated against the membership
 * list there — see the open-redirect guard note above); `next` carries
 * the path they were on so the deep link survives the hop (validated
 * again where it is composed — `composeWorkspaceUrl`). Returns null when
 * there is no central host for this origin (localhost / unknown host) —
 * callers render the inline login page then.
 */
export function buildCentralLoginUrl(
  host: string,
  options: CentralLoginUrlOptions = {},
): string | null {
  const centralHost = centralHostForTenantHost(host)
  if (centralHost === null || isCentralAuthHost(host)) return null
  const hostname = host.split(':')[0] ?? ''
  const label = hostname.slice(0, -APEX.length)
  const tenantSub = label.replace(/-staging$|-prod$/, '')
  if (!SAFE_TENANT_SUBDOMAIN_RE.test(tenantSub)) return null
  const params = new URLSearchParams({ return: tenantSub })
  const next = sanitizeNextPath(options.next)
  if (next !== null) params.set('next', next)
  return `https://${centralHost}/login?${params.toString()}`
}
